import React, { useState, useRef, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { getLocationSuggestions } from 'services/fuzzySuggestionsService';
import { useUserDetailsContext } from 'context/userDetailsContext';

const LocationInputWithSuggestions = ({ value, onChange, placeholder = 'Add new location', className = '' }) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const { userDetails } = useUserDetailsContext();

  // Using API data (hardcoded data removed)
  const USE_HARDCODED_DATA = false;

  // Sync inputValue with value prop
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Get suggestions from API
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (!inputValue.trim()) {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Debounce API calls
    const timeout = setTimeout(async () => {
      try {
        setIsLoading(true);
        console.log('🔍 Fetching location suggestions for:', inputValue.trim());
        const suggestions = await getLocationSuggestions(userDetails?.tenant_id, inputValue.trim());
        console.log('📡 API Response:', suggestions);

        // Ensure suggestions is an array and each item has proper structure
        let validSuggestions = [];

        if (Array.isArray(suggestions)) {
          validSuggestions = suggestions
            .map((suggestion) => {
              // Handle different response formats
              if (typeof suggestion === 'string') {
                return suggestion;
              } else if (suggestion && suggestion.label) {
                return suggestion.label; // Use the transformed label from API
              } else if (suggestion && (suggestion.title || suggestion.name)) {
                return suggestion.title || suggestion.name;
              }
              return String(suggestion);
            })
            .filter((suggestion) => suggestion && suggestion.trim());
        }

        console.log('✅ Valid suggestions:', validSuggestions);
        setFilteredSuggestions(validSuggestions);
        setShowSuggestions(validSuggestions.length > 0);
      } catch (error) {
        console.error('❌ Error fetching location suggestions:', error);
        setFilteredSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setIsLoading(false);
      }
    }, 300); // 300ms debounce

    setSearchTimeout(timeout);

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [inputValue]);

  // Detect dark mode
  useEffect(() => {
    const detectDarkMode = () => {
      const isDark =
        document.documentElement.getAttribute('data-theme') === 'dark' ||
        (!document.documentElement.getAttribute('data-theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
      setIsDarkMode(isDark);
    };

    detectDarkMode();

    // Watch for theme changes
    const observer = new MutationObserver(detectDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    // Watch for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', detectDarkMode);

    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener('change', detectDarkMode);
    };
  }, []);

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
    onChange(suggestion);
    setShowSuggestions(false);
  };

  // Handle input focus
  const handleFocus = () => {
    if (filteredSuggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  // Handle input blur
  const handleBlur = (e) => {
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="position-relative" style={{ width: '100%' }}>
      <InputText
        ref={inputRef}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={`w-100 ${className}`}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '1px solid #dce4f2',
          borderRadius: '4px',
          fontSize: '14px',
          backgroundColor: '#fff',
          color: '#333',
        }}
      />

      {isLoading && (
        <div
          style={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '12px',
            color: '#666',
          }}
        >
          Loading...
        </div>
      )}

      {showSuggestions && filteredSuggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 10000,
            backgroundColor: isDarkMode ? 'var(--dropdown-bg)' : '#f5f5f5', // Standard dropdown background
            border: 'none', // Remove border
            borderRadius: '6px', // Smaller border radius
            boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)', // Theme-aware shadow
            maxHeight: '200px',
            overflowY: 'auto',
            marginTop: '4px',
            fontSize: '13px', // Smaller font size
            lineHeight: '1.3', // Tighter line height
          }}
        >
          {/* Header banner */}
          <div
            style={{
              backgroundColor: isDarkMode ? 'var(--dropdown-item)' : '#e8e8e8', // Standard dropdown item background
              padding: '6px 12px', // Smaller padding
              fontSize: '12px', // Smaller font size
              fontWeight: '600', // Semibold
              color: isDarkMode ? 'var(--primary-font)' : '#333', // Standard primary font color
              borderRadius: '6px 6px 0 0', // Match container border radius
              borderBottom: 'none', // Remove border
              transition: 'background-color 0.2s ease', // Smooth background transition only
            }}
          >
            {filteredSuggestions.length} similar location{filteredSuggestions.length !== 1 ? 's' : ''} found
          </div>

          {filteredSuggestions.map((suggestion, index) => (
            <div
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              style={{
                padding: '10px 12px', // Smaller padding
                cursor: 'pointer',
                color: isDarkMode ? 'var(--primary-font)' : '#333', // Standard primary font color
                backgroundColor: 'transparent',
                borderBottom: 'none', // Remove borders
                fontSize: '13px', // Smaller font size
                transition: 'background-color 0.2s ease',
                lineHeight: '1.3', // Tighter line height
                borderRadius: index === filteredSuggestions.length - 1 ? '0 0 6px 6px' : '0', // Match container border radius
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = isDarkMode ? 'var(--selected-msg)' : '#fff8e1'; // Standard selected message background for dark mode, yellow for light mode
                // Only change header background color, keep text and font same
                const headerElement = suggestionsRef.current?.querySelector('div:first-child');
                if (headerElement) {
                  headerElement.style.backgroundColor = isDarkMode ? 'var(--action-bg-2)' : '#d0d0d0'; // Standard action background for dark mode, light gray for light mode
                  // Keep text color and font weight same
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                // Only reset header background color
                const headerElement = suggestionsRef.current?.querySelector('div:first-child');
                if (headerElement) {
                  headerElement.style.backgroundColor = isDarkMode ? 'var(--dropdown-item)' : '#e8e8e8'; // Standard dropdown item background
                  // Keep text color and font weight same
                }
              }}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationInputWithSuggestions;
